import {
    requiredTranslatableZodSchema,
    toTranslatableString,
} from "@@/shared/translations/translation";
import { z } from "zod";
import type { ProductFormFieldId } from "../product-form-field-id";
import {
    productFormFieldIdFactory,
    productFormFieldIdZodSchema,
} from "../product-form-field-id";
import { productFormFieldGroupByZodUnion } from "../product-form-field-quantity";
import { productFormFieldSelectOptionZodObject } from "./product-form-field.select-option";

const _productFormSelectAnswerZodObject = z.object({
    _id: productFormFieldIdZodSchema,
    _type: z.literal("PRODUCT_FORM_FIELD_ANSWER"),
    type: z.literal("SELECT"),
    title: requiredTranslatableZodSchema,
    // TODO! Add support for multi choice and multi select
    // TODO! Since we do not have that yet, for now, "There can be only one"
    // TODO! NOTE: When you do, values need to switch to an
    // TODO! NOTE: array of an array of strings
    required: z.boolean(),
    minRequired: z.number().int().positive().min(1).max(1),
    maxRequired: z.number().int().positive().min(1).max(1),
    options: z.array(productFormFieldSelectOptionZodObject),
    quantity: z.number().int().positive().min(1),
    values: z.record(z.coerce.number(), z.string().min(1)),
    groupedBy: productFormFieldGroupByZodUnion,
});

const productFormSelectAnswerZodObject = _productFormSelectAnswerZodObject
    .refine(
        data => {
            if (!data.required) return true;
            if (data.quantity !== Object.keys(data.values).length) return false;
            return true;
        },
        data => ({
            message: toTranslatableString({
                sv: "Antalet svar stämmer inte överens med det begärda antalet svar.",
                en: "The number of answers does not match the requested number of answers.",
            }),
            params: {
                quantity: data.quantity,
                values: data.values,
            },
            path: ["values"],
        }),
    )
    .refine(
        data => {
            if (!data.required) return true;
            if (data.minRequired > Object.keys(data.values).length)
                return false;
            return true;
        },
        data => ({
            message: toTranslatableString({
                sv: `Du måste välja minst ${data.minRequired} alternativ.`,
                en: `You must select at least ${data.minRequired} options.`,
            }),
            params: {
                minRequired: data.minRequired,
                count: Object.keys(data.values).length,
            },
            path: ["values"],
        }),
    );

type ProductFormField_SelectAnswer = z.infer<
    typeof productFormSelectAnswerZodObject
>;

const _factoryParamsZodObject = _productFormSelectAnswerZodObject.omit({
    _id: true,
    _type: true,
    type: true,
});

type FactoryParams = z.infer<typeof _factoryParamsZodObject>;
const productFormSelectAnswerFactory = (
    input: FactoryParams & {
        _id?: ProductFormFieldId;
    },
): ProductFormField_SelectAnswer => {
    return {
        ...input,
        _id: input._id ?? productFormFieldIdFactory(),
        _type: "PRODUCT_FORM_FIELD_ANSWER",
        type: "SELECT",
    };
};

export { productFormSelectAnswerFactory, productFormSelectAnswerZodObject };
export type {
    ProductFormField_SelectAnswer,
    FactoryParams as ProductFormField_SelectAnswerFactoryParams,
};
